import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fmtDate } from "@/lib/api";
import { ArrowLeft, Plus, X, Download, Trash2, Upload, FolderKanban } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import StatusPill from "@/components/StatusPill";
import ProgressRing from "@/components/ProgressRing";

const CI_KAT = [
  { id: "ci_manual", label: "CI-Manual" },
  { id: "logo", label: "Logos" },
  { id: "schrift", label: "Schriften" },
  { id: "designbeispiel", label: "Designbeispiele" },
  { id: "sonstiges", label: "Sonstiges" },
];

const TABS = [
  { id: "daten", label: "Kundendaten" },
  { id: "ap", label: "Ansprechpartner" },
  { id: "projekte", label: "Projekte" },
  { id: "ci", label: "CI-Materialien" },
];

export default function KundeDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const canEdit = user?.rolle === "admin" || user?.rolle === "pm";
  const [tab, setTab] = useState("daten");
  const [kunde, setKunde] = useState(null);
  const [files, setFiles] = useState([]);

  const load = async () => {
    const [k, f] = await Promise.all([api.get(`/kunden/${id}`), api.get(`/kunden/${id}/dateien`)]);
    setKunde(k.data);
    setFiles(f.data);
  };
  useEffect(() => { load(); }, [id]);

  if (!kunde) return <div className="mono-label">Lade Kunde…</div>;

  return (
    <div>
      <Link to="/kunden" className="mono-label inline-flex items-center gap-1.5 hover:opacity-100 mb-4"><ArrowLeft size={12} /> Zurück</Link>

      <div className="mb-6 flex items-center gap-5">
        {kunde.logo && (
          <div className="w-20 h-20 border border-line rounded-sm bg-white flex items-center justify-center overflow-hidden shrink-0">
            <img src={kunde.logo} alt={kunde.firmenname} className="max-w-full max-h-full object-contain" data-testid="kunde-header-logo" />
          </div>
        )}
        <div>
          <div className="mono-label">Kunde</div>
          <h1 className="font-archivo font-bold text-4xl mt-1">{kunde.firmenname}</h1>
        </div>
      </div>

      <div className="flex gap-1 mb-6 border-b border-line">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`kunde-tab-${t.id}`}
            className={`px-4 py-3 text-sm border-b-2 -mb-px transition-colors ${
              tab === t.id ? "border-ink text-ink font-medium" : "border-transparent text-ink/50 hover:text-ink"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "daten" && <DatenTab kunde={kunde} canEdit={canEdit} onReload={load} />}
      {tab === "ap" && <APTab kunde={kunde} canEdit={canEdit} onReload={load} />}
      {tab === "projekte" && <ProjekteTab kundeId={id} />}
      {tab === "ci" && <CITab kundeId={id} files={files} canEdit={canEdit} onReload={load} />}
    </div>
  );
}

function ProjekteTab({ kundeId }) {
  const [projekte, setProjekte] = useState([]);
  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/kunden/${kundeId}/projekte`);
      setProjekte(data);
    })();
  }, [kundeId]);

  const active = projekte.filter((p) => !p.archiviert);
  const archived = projekte.filter((p) => p.archiviert);

  const Row = ({ p }) => (
    <Link to={`/projekte/${p.id}`} className="flex items-center gap-4 px-5 py-4 border-b border-line/60 last:border-0 hover:bg-paper/40" data-testid={`kunde-projekt-${p.id}`}>
      <ProgressRing pct={p.fortschritt_pct} size={40} stroke={4} />
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm truncate">{p.titel}</div>
        <div className="mono-label mt-0.5">{p.bereich} · {p.designer_name} · Start {fmtDate(p.startdatum)}</div>
      </div>
      <div className="font-mono text-xs text-ink/60 w-24 text-right">{p.laufend ? "laufend" : fmtDate(p.deadline)}</div>
      <StatusPill status={p.status} />
    </Link>
  );

  return (
    <div className="space-y-4">
      <div className="paper-card overflow-hidden">
        <div className="px-5 py-3 border-b border-line bg-paper/60 flex items-center justify-between">
          <div className="font-archivo font-bold text-sm inline-flex items-center gap-2"><FolderKanban size={14} /> Aktive Projekte</div>
          <span className="mono-label">{active.length}</span>
        </div>
        {active.length === 0 ? (
          <div className="p-8 text-center text-ink/50 text-sm">Keine aktiven Projekte für diesen Kunden.</div>
        ) : active.map((p) => <Row key={p.id} p={p} />)}
      </div>

      {archived.length > 0 && (
        <div className="paper-card overflow-hidden">
          <div className="px-5 py-3 border-b border-line bg-paper/60 flex items-center justify-between">
            <div className="font-archivo font-bold text-sm">Archiv</div>
            <span className="mono-label">{archived.length}</span>
          </div>
          {archived.map((p) => <Row key={p.id} p={p} />)}
        </div>
      )}
    </div>
  );
}

function DatenTab({ kunde, canEdit, onReload }) {
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    firmenname: kunde.firmenname || "",
    adresse: kunde.adresse || "",
    domain: kunde.domain || "",
    notizen: kunde.notizen || "",
    ci_beschreibung: kunde.ci_beschreibung || "",
    stundensaetze: kunde.stundensaetze || [],
    logo: kunde.logo || "",
  });

  const addSatz = () => setF((s) => ({ ...s, stundensaetze: [...s.stundensaetze, { bezeichnung: "", satz: 0 }] }));
  const remSatz = (i) => setF((s) => ({ ...s, stundensaetze: s.stundensaetze.filter((_, idx) => idx !== i) }));
  const updSatz = (i, k, v) => setF((s) => ({ ...s, stundensaetze: s.stundensaetze.map((sa, idx) => idx === i ? { ...sa, [k]: k === "satz" ? parseFloat(v) || 0 : v } : sa) }));

  const handleLogo = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const r = new FileReader();
    r.onload = () => setF((s) => ({ ...s, logo: r.result }));
    r.readAsDataURL(file);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/kunden/${kunde.id}`, f);
      await onReload();
    } finally { setBusy(false); }
  };

  return (
    <div className="paper-card p-8 space-y-6">
      <section>
        <h3 className="mono-label mb-3">Kundenlogo</h3>
        <div className="flex items-center gap-4">
          <div className="w-24 h-24 border border-line rounded-sm bg-white flex items-center justify-center overflow-hidden">
            {f.logo ? <img src={f.logo} alt="Logo" className="max-w-full max-h-full object-contain" data-testid="kunde-logo-preview" /> : <span className="mono-label">kein Logo</span>}
          </div>
          {canEdit && (
            <div>
              <label className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-line rounded-sm text-xs cursor-pointer hover:border-ink bg-white">
                <Upload size={11} /> Logo hochladen
                <input type="file" accept="image/*" onChange={handleLogo} className="hidden" data-testid="kunde-logo-upload" />
              </label>
              {f.logo && (
                <button type="button" onClick={() => setF((s) => ({ ...s, logo: "" }))} className="mono-label ml-3 text-brunnerMagenta hover:opacity-100">
                  Entfernen
                </button>
              )}
              <div className="mono-label mt-2" style={{opacity:1}}>PNG oder JPG, wird in der Kundenübersicht und im Projekt angezeigt.</div>
            </div>
          )}
        </div>
      </section>

      <section>
        <h3 className="mono-label mb-3">Basisdaten</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="mono-label block mb-1.5">Firmenname</label>
            <input value={f.firmenname} onChange={(e) => setF({ ...f, firmenname: e.target.value })} disabled={!canEdit} data-testid="kunde-edit-firmenname" className="w-full px-3 py-2 border border-line rounded-sm bg-white disabled:bg-paper" />
          </div>
          <div>
            <label className="mono-label block mb-1.5">Domain / Webseite</label>
            <input value={f.domain} onChange={(e) => setF({ ...f, domain: e.target.value })} disabled={!canEdit} data-testid="kunde-edit-domain" placeholder="firma.at" className="w-full px-3 py-2 border border-line rounded-sm bg-white disabled:bg-paper" />
          </div>
          <div className="md:col-span-2">
            <label className="mono-label block mb-1.5">Adresse</label>
            <input value={f.adresse} onChange={(e) => setF({ ...f, adresse: e.target.value })} disabled={!canEdit} data-testid="kunde-edit-adresse" className="w-full px-3 py-2 border border-line rounded-sm bg-white disabled:bg-paper" />
          </div>
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="mono-label">Kundenstundensätze</h3>
          {canEdit && <button onClick={addSatz} className="mono-label hover:opacity-100 inline-flex items-center gap-1"><Plus size={11} /> Satz hinzufügen</button>}
        </div>
        <div className="mono-label mb-2" style={{opacity: 1}}>Bezeichnung + Satz je Leistungsart. Wird beim Anlegen von Projekten mit Modell „Stundenbasis" oder „Kontingent" ausgewählt.</div>
        <div className="space-y-2">
          {f.stundensaetze.length === 0 && <div className="text-sm text-ink/50">Keine Sätze hinterlegt.</div>}
          {f.stundensaetze.map((s, i) => (
            <div key={i} className="grid grid-cols-[2fr_1fr_auto] gap-3 items-center">
              <input value={s.bezeichnung} placeholder="z. B. Beratung, Design, Standard" onChange={(e) => updSatz(i, "bezeichnung", e.target.value)} disabled={!canEdit} data-testid={`satz-bez-${i}`} className="px-3 py-2 border border-line rounded-sm text-sm disabled:bg-paper" />
              <div className="flex items-center gap-2">
                <input type="number" step="0.5" value={s.satz} onChange={(e) => updSatz(i, "satz", e.target.value)} disabled={!canEdit} data-testid={`satz-val-${i}`} className="w-full px-3 py-2 border border-line rounded-sm text-sm disabled:bg-paper" />
                <span className="mono-label" style={{opacity:1}}>€/h</span>
              </div>
              {canEdit && <button onClick={() => remSatz(i)} className="text-ink/40 hover:text-brunnerMagenta p-1"><X size={14} /></button>}
            </div>
          ))}
        </div>
      </section>

      <section>
        <h3 className="mono-label mb-3">Notizen</h3>
        <textarea rows={3} value={f.notizen} onChange={(e) => setF({ ...f, notizen: e.target.value })} disabled={!canEdit} data-testid="kunde-edit-notizen" className="w-full px-3 py-2 border border-line rounded-sm bg-white disabled:bg-paper" placeholder="Wichtige Informationen zum Kunden, Freigabe-Prozess, Zahlungsbedingungen..." />
      </section>

      <section>
        <h3 className="mono-label mb-3">CI-Kurzbeschreibung</h3>
        <textarea rows={3} value={f.ci_beschreibung} onChange={(e) => setF({ ...f, ci_beschreibung: e.target.value })} disabled={!canEdit} data-testid="kunde-edit-ci" className="w-full px-3 py-2 border border-line rounded-sm bg-white disabled:bg-paper" placeholder="Kurzcharakterisierung der Corporate Identity (Farben, Tonalität, Stil)..." />
      </section>

      {canEdit && (
        <div className="pt-4 border-t border-line flex justify-between items-center">
          <div className="mono-label">Erstellt am {fmtDate(kunde.erstellt_am)}</div>
          <button onClick={save} disabled={busy} data-testid="kunde-daten-save" className="px-4 py-2 bg-ink text-white rounded-sm hover:bg-black disabled:opacity-60">
            {busy ? "Speichern…" : "Speichern"}
          </button>
        </div>
      )}
    </div>
  );
}

function APTab({ kunde, canEdit, onReload }) {
  const [list, setList] = useState(kunde.ansprechpartner || []);
  const [busy, setBusy] = useState(false);

  const add = () => setList([...list, { name: "", email: "", telefon: "" }]);
  const rem = (i) => setList(list.filter((_, idx) => idx !== i));
  const upd = (i, k, v) => setList(list.map((a, idx) => (idx === i ? { ...a, [k]: v } : a)));

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/kunden/${kunde.id}`, { ansprechpartner: list.filter((a) => a.name.trim()) });
      await onReload();
    } finally { setBusy(false); }
  };

  return (
    <div className="paper-card p-8">
      {list.length === 0 && <div className="text-ink/50 mb-4">Noch keine Ansprechpartner.</div>}
      <div className="space-y-3">
        {list.map((a, i) => (
          <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-start border border-line rounded-sm p-3">
            <input placeholder="Name" value={a.name} disabled={!canEdit} onChange={(e) => upd(i, "name", e.target.value)} className="px-3 py-2 border border-line rounded-sm text-sm disabled:bg-paper" />
            <input placeholder="E-Mail" value={a.email} disabled={!canEdit} onChange={(e) => upd(i, "email", e.target.value)} className="px-3 py-2 border border-line rounded-sm text-sm disabled:bg-paper" />
            <input placeholder="Telefon" value={a.telefon} disabled={!canEdit} onChange={(e) => upd(i, "telefon", e.target.value)} className="px-3 py-2 border border-line rounded-sm text-sm disabled:bg-paper" />
            {canEdit && <button onClick={() => rem(i)} className="text-ink/40 hover:text-brunnerMagenta p-2"><X size={14} /></button>}
          </div>
        ))}
      </div>
      {canEdit && (
        <div className="mt-6 pt-4 border-t border-line flex justify-between items-center">
          <button onClick={add} data-testid="ap-add" className="mono-label hover:opacity-100 inline-flex items-center gap-1"><Plus size={11} /> Ansprechpartner hinzufügen</button>
          <button onClick={save} disabled={busy} data-testid="ap-save" className="px-4 py-2 bg-ink text-white rounded-sm hover:bg-black disabled:opacity-60">
            {busy ? "Speichern…" : "Speichern"}
          </button>
        </div>
      )}
    </div>
  );
}

function CITab({ kundeId, files, canEdit, onReload }) {
  const [kat, setKat] = useState("ci_manual");
  const [dataUrl, setDataUrl] = useState(null);
  const [fileName, setFileName] = useState("");
  const [beschr, setBeschr] = useState("");
  const [busy, setBusy] = useState(false);

  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setFileName(f.name);
    const r = new FileReader();
    r.onload = () => setDataUrl(r.result);
    r.readAsDataURL(f);
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!dataUrl) return;
    setBusy(true);
    try {
      await api.post(`/kunden/${kundeId}/dateien`, {
        kategorie: kat, dateiname: fileName, datei: dataUrl, beschreibung: beschr,
      });
      setDataUrl(null); setFileName(""); setBeschr("");
      await onReload();
    } finally { setBusy(false); }
  };

  const remove = async (fid) => {
    if (!window.confirm("Datei löschen?")) return;
    await api.delete(`/kunden/${kundeId}/dateien/${fid}`);
    await onReload();
  };

  const openFile = (dataUrl, name) => {
    const w = window.open();
    if (!w) return;
    if (dataUrl.startsWith("data:application/pdf")) {
      w.document.write(`<iframe src="${dataUrl}" style="border:0;width:100vw;height:100vh"></iframe>`);
    } else if (dataUrl.startsWith("data:image/")) {
      w.document.write(`<img src="${dataUrl}" style="max-width:100%" alt="${name}"/>`);
    } else {
      const a = document.createElement("a"); a.href = dataUrl; a.download = name; a.click();
    }
  };

  const byKat = CI_KAT.reduce((acc, k) => { acc[k.id] = files.filter((f) => f.kategorie === k.id); return acc; }, {});

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {canEdit && (
        <div className="lg:col-span-1 paper-card p-6 h-fit">
          <h3 className="font-archivo font-bold text-lg mb-4">Datei hochladen</h3>
          <form onSubmit={upload} className="space-y-3" data-testid="ci-form">
            <div>
              <label className="mono-label block mb-1">Kategorie</label>
              <select value={kat} onChange={(e) => setKat(e.target.value)} data-testid="ci-kat" className="w-full px-3 py-2 border border-line rounded-sm bg-white">
                {CI_KAT.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
              </select>
            </div>
            <div>
              <label className="mono-label block mb-1">Datei (PDF, Bild, ZIP…)</label>
              <input type="file" onChange={onFile} data-testid="ci-file" className="text-sm w-full" />
              {fileName && <div className="mono-label mt-1" style={{opacity:1}}>{fileName}</div>}
            </div>
            <div>
              <label className="mono-label block mb-1">Beschreibung</label>
              <input value={beschr} onChange={(e) => setBeschr(e.target.value)} data-testid="ci-beschr" className="w-full px-3 py-2 border border-line rounded-sm text-sm" placeholder="z. B. Primärlogo RGB" />
            </div>
            <button type="submit" disabled={busy || !dataUrl} data-testid="ci-submit" className="w-full py-2 bg-ink text-white rounded-sm hover:bg-black disabled:opacity-60 inline-flex items-center justify-center gap-2">
              <Upload size={14} /> {busy ? "Lädt…" : "Hochladen"}
            </button>
          </form>
        </div>
      )}

      <div className={`${canEdit ? "lg:col-span-2" : "lg:col-span-3"} space-y-4`}>
        {CI_KAT.map((k) => (
          <div key={k.id} className="paper-card p-5">
            <div className="flex items-baseline justify-between mb-3">
              <h4 className="font-archivo font-bold text-base">{k.label}</h4>
              <span className="mono-label">{byKat[k.id].length} Datei{byKat[k.id].length === 1 ? "" : "en"}</span>
            </div>
            {byKat[k.id].length === 0 && <div className="text-sm text-ink/50">Keine Dateien.</div>}
            <div className="space-y-2">
              {byKat[k.id].map((f) => (
                <div key={f.id} className="flex items-center justify-between gap-3 px-3 py-2 border border-line rounded-sm hover:bg-paper/40" data-testid={`ci-file-${f.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate">{f.dateiname}</div>
                    <div className="mono-label mt-0.5">{fmtDate(f.erstellt_am)} · {f.hochgeladen_von}{f.beschreibung ? ` · ${f.beschreibung}` : ""}</div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => openFile(f.datei, f.dateiname)} data-testid={`ci-open-${f.id}`} className="inline-flex items-center gap-1 px-2 py-1 border border-line rounded-sm text-xs hover:border-ink">
                      <Download size={11} /> Öffnen
                    </button>
                    {canEdit && (
                      <button onClick={() => remove(f.id)} className="p-1 text-ink/40 hover:text-brunnerMagenta"><Trash2 size={12} /></button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
